import React, { useEffect, useState, useMemo } from "react";
import { fetchRates, fetchGold, formatNum } from "../lib/api";
import { ArrowRightLeft, Loader2 } from "lucide-react";

export default function CurrencyConverter({ compact = false }) {
  const [rates, setRates] = useState([]);
  const [gold, setGold] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("100");
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("TRY");

  useEffect(() => {
    Promise.all([fetchRates(), fetchGold()])
      .then(([r, g]) => {
        setRates(r.rates);
        setGold(g.items);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Build conversion map: code -> TRY value
  const tryMap = useMemo(() => {
    const map = { TRY: 1 };
    rates.forEach((r) => {
      if (r.forex_selling) {
        map[r.code] = r.forex_selling / (r.unit || 1);
      }
    });
    const gram = gold.find((x) => x.code === "GRAM");
    if (gram) map["GRAM_GOLD"] = gram.sell;
    return map;
  }, [rates, gold]);

  const result = useMemo(() => {
    const amt = parseFloat(String(amount).replace(",", "."));
    if (Number.isNaN(amt) || !tryMap[from] || !tryMap[to]) return null;
    const inTry = amt * tryMap[from];
    return inTry / tryMap[to];
  }, [amount, from, to, tryMap]);

  const swap = () => {
    setFrom(to);
    setTo(from);
  };

  const options = useMemo(() => {
    const list = [
      { code: "TRY", label: "🇹🇷 Türk Lirası" },
      ...rates.map((r) => ({ code: r.code, label: `${r.flag || ""} ${r.code} - ${r.name}` })),
    ];
    if (gold.find((g) => g.code === "GRAM")) {
      list.push({ code: "GRAM_GOLD", label: "🥇 Gram Altın" });
    }
    return list;
  }, [rates, gold]);

  return (
    <div
      className={`relative bg-white rounded-2xl border border-slate-200 shadow-2xl shadow-slate-900/5 ${compact ? "p-6" : "p-7 md:p-8"}`}
      data-testid="currency-converter-widget"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-brand-gold font-bold">
            Döviz Çevirici
          </div>
          <h3 className="font-display text-xl md:text-2xl font-bold text-slate-950 mt-1">
            Anlık Hesapla
          </h3>
        </div>
        <span className="live-dot" />
      </div>

      {loading ? (
        <div className="h-44 flex items-center justify-center text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-[1fr_auto] gap-3 items-stretch">
            <input
              type="number"
              step="any"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-brand-gold focus:outline-none focus:ring-2 focus:ring-brand-gold/20 font-mono-fin text-lg text-slate-950"
              data-testid="converter-amount-input"
              placeholder="Tutar"
            />
            <select
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="px-4 rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-brand-gold text-sm font-medium text-slate-950 cursor-pointer min-w-[140px]"
              data-testid="converter-from-select"
            >
              {options.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-center">
            <button
              type="button"
              onClick={swap}
              className="p-2 rounded-full bg-slate-950 text-brand-gold hover:rotate-180 transition-transform duration-500"
              aria-label="Para birimlerini değiştir"
              data-testid="converter-swap-button"
            >
              <ArrowRightLeft className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-[1fr_auto] gap-3 items-stretch">
            <div
              className="w-full px-4 py-3.5 rounded-xl bg-slate-950 text-brand-gold font-mono-fin text-lg overflow-hidden"
              data-testid="converter-result"
            >
              {result === null ? "—" : formatNum(result, 4)}
            </div>
            <select
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="px-4 rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-brand-gold text-sm font-medium text-slate-950 cursor-pointer min-w-[140px]"
              data-testid="converter-to-select"
            >
              {options.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <p className="text-[11px] text-slate-500 pt-2 border-t border-slate-100">
            Kurlar anlık piyasa satış değerleri üzerinden hesaplanır. Bilgilendirme amaçlıdır.
          </p>
        </div>
      )}
    </div>
  );
}
